import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { backendService } from "../services/backend.service";
import { getShiftDisplayDate } from "../utils/dateUtils";
import { formatPersonFullName, isNurseSchedulableInDepartment } from "../utils/formatters";
import type { Nurse, NurseFullRole, NurseInShift, OvertimeKind } from "../utils/models";
import { inverseRolesMap } from "../utils/utilFunctions";
import { useDepartmentFlags } from "./useDepartmentFlags";
import { useLastNameFirst } from "./useNameFormat";
import { useFeatureFlag } from "./useOpenFeatureFlag";
import { useRosterLookup } from "./useRosterLookup";
import { useSwapOptions } from "./useSwapOptions";

export type NurseOption = {
	id: number;
	label: string;
	nurse: Nurse;
};

/** No one, until the roster arrives. */
const NO_NURSES: Nurse[] = [];

const formatNurseOption = (nurse: Nurse, lastNameFirst: boolean): NurseOption => ({
	id: nurse.id,
	label: formatPersonFullName(nurse, lastNameFirst),
	nurse,
});

const areShiftsCompatible = (originStart: DateTime, originEnd: DateTime, swapStart: DateTime, swapEnd: DateTime): boolean => {
	const overlapStart = originStart > swapStart ? originStart : swapStart;
	const overlapEnd = originEnd < swapEnd ? originEnd : swapEnd;
	const overlapHours = overlapEnd.diff(overlapStart, "hour").as("hours");

	return overlapHours < 1;
};

const getRoleGroupForRole = (roleGroups: Record<string, string[]> | undefined, role: string): string | undefined => {
	if (!roleGroups) return undefined;
	const roleKey = inverseRolesMap[role as NurseFullRole];
	return Object.entries(roleGroups).find(([_, roles]) => roles.includes(roleKey))?.[0];
};

const areRoleGroupsCompatible = (roleGroups: Record<string, string[]> | undefined, originRole: string, swapRole: string): boolean => {
	if (!roleGroups) return originRole === swapRole;

	const originRoleGroup = getRoleGroupForRole(roleGroups, originRole);
	const swapRoleGroup = getRoleGroupForRole(roleGroups, swapRole);

	if (originRoleGroup) {
		return swapRoleGroup === originRoleGroup;
	}

	return originRole === swapRole;
};

type SwapRequestHookParams = {
	originDate: DateTime | null;
	swapDate: DateTime | null;
	initialShift?: NurseInShift;
};

/** DAT-289: the predicted delta is a MAX across weekly/daily/consecutive, so the KIND has to travel with
 * the hours — the dialog previously called all three "weekly overtime", which is simply false for a
 * part-timer whose only overtime is the 7th-consecutive-day premium.
 *
 * `null` is "this swap moves no overtime", which is why there is no zero-hours sentinel: a placeholder
 * would need a kind, and inventing one is the bug this type exists to prevent. */
export type SwapOvertime = { hours: number; kind: OvertimeKind } | null;

const DEFAULT_OT: { originOT: SwapOvertime; swapOT: SwapOvertime } = { originOT: null, swapOT: null };

/** The prediction only describes overtime when a cap actually produced it — otherwise there is nothing
 * to render and nothing to name. */
const toSwapOvertime = (hours: number, kind: OvertimeKind | null): SwapOvertime => (kind === null || hours === 0 ? null : { hours, kind });

export type SwapSideData = {
	nurse: NurseOption | null;
	shift: NurseInShift | null;
	nurseOptions: NurseOption[];
	shiftOptions: NurseInShift[];
	onNurseChange: (nurse: NurseOption | null) => void;
	onShiftChange: (shift: NurseInShift | null) => void;
	willCauseOvertime: { originOT: SwapOvertime; swapOT: SwapOvertime };
};

type SwapRequestHookReturn = {
	origin: SwapSideData;
	swap: SwapSideData;
	hasShiftInDate: (nurseId: number, date: DateTime) => boolean;
};

export const useSwapRequest = ({ originDate, swapDate, initialShift }: SwapRequestHookParams): SwapRequestHookReturn => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const { roster, getNurseById } = useRosterLookup(chosenDepartment.department_id);
	// DEFAULT: nobody to swap with until the roster arrives (the provider has loaded it before a panel opens).
	const nurses = roster ?? NO_NURSES;
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const initialOriginNurse = useMemo(() => {
		if (!initialShift) return null;
		const nurse = getNurseById(initialShift.nurseId);
		return nurse ? formatNurseOption(nurse, lastNameFirst) : null;
	}, [initialShift, getNurseById, lastNameFirst]);

	const [originNurse, setOriginNurse] = useState<NurseOption | null>(initialOriginNurse);
	const [swapNurse, setSwapNurse] = useState<NurseOption | null>(null);
	const [originShift, setOriginShift] = useState<NurseInShift | null>(initialShift ?? null);
	const [swapShift, setSwapShift] = useState<NurseInShift | null>(null);

	const swapOptionsDateRange = useMemo(() => {
		const baseDate = DateTime.now().startOf("week");
		return {
			start: baseDate,
			end: baseDate.plus({ months: 2 }),
		};
	}, []);

	const { data: swapOptionsData, isLoading: isLoadingSwapOptions } = useSwapOptions(
		chosenDepartment.department_id,
		swapOptionsDateRange.start,
		swapOptionsDateRange.end,
	);

	const allAvailableShifts = useMemo<NurseInShift[]>(() => {
		if (isLoadingSwapOptions || !swapOptionsData) return [];
		return Object.values(swapOptionsData).flat();
	}, [isLoadingSwapOptions, swapOptionsData]);

	const getShiftsForDate = useCallback(
		(date: DateTime | null, shifts: NurseInShift[]) => {
			if (!date) return [];
			const normalizedDate = date.startOf("day");
			return shifts.filter((shift) => {
				const shiftDateInTz = shift.startTime.setZone(timezone);
				const shiftDate = getShiftDisplayDate(shiftDateInTz, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
				return shiftDate.toFormat("yyyy-MM-dd") === normalizedDate.toFormat("yyyy-MM-dd");
			});
		},
		[timezone, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const originShiftsForDate = useMemo(() => getShiftsForDate(originDate, allAvailableShifts), [allAvailableShifts, originDate, getShiftsForDate]);
	const swapShiftsForDate = useMemo(() => getShiftsForDate(swapDate, allAvailableShifts), [allAvailableShifts, swapDate, getShiftsForDate]);

	const lockedOriginNurse = initialOriginNurse;

	const originNurseOptions = useMemo<NurseOption[]>(() => {
		return nurses.map((nurse) => formatNurseOption(nurse, lastNameFirst));
	}, [nurses, lastNameFirst]);

	const swapNurseIdSet = useMemo(() => new Set(allAvailableShifts.map((shift) => shift.nurseId)), [allAvailableShifts]);

	const baseSwapNurseOptions = useMemo<NurseOption[]>(() => {
		return nurses
			.filter(
				(nurse) => swapNurseIdSet.has(nurse.id) && nurse.id !== originNurse?.nurse?.id && isNurseSchedulableInDepartment(nurse, chosenDepartment.department_id),
			)
			.map((nurse) => formatNurseOption(nurse, lastNameFirst));
	}, [swapNurseIdSet, nurses, originNurse, chosenDepartment.department_id, lastNameFirst]);

	const filterCompatibleSwapNurses = useCallback(
		(nurses: NurseOption[], originShiftSelection: NurseInShift): NurseOption[] => {
			const originNurseData = getNurseById(originShiftSelection.nurseId);
			if (!originNurseData) return [];

			return nurses.filter((nurseOption) => {
				const swapNurseData = getNurseById(nurseOption.id);
				if (!swapNurseData) return false;

				// When job management is on, filter by job compatibility; otherwise fall back to role group check
				if (jobManagementEnabled && originShiftSelection.jobId) {
					const hasMatchingJobShift = allAvailableShifts.some((shift) => shift.nurseId === nurseOption.id && shift.jobId === originShiftSelection.jobId);
					if (!hasMatchingJobShift) return false;
				} else if (chosenDepartment.role_groups && !areRoleGroupsCompatible(chosenDepartment.role_groups, originNurseData.role, swapNurseData.role)) {
					return false;
				}

				const nurseShifts = allAvailableShifts.filter((shift) => shift.nurseId === nurseOption.id);
				return !nurseShifts.some((shift) => !areShiftsCompatible(originShiftSelection.startTime, originShiftSelection.endTime, shift.startTime, shift.endTime));
			});
		},
		[getNurseById, chosenDepartment.role_groups, allAvailableShifts, jobManagementEnabled],
	);

	const swapNurseOptions = useMemo<NurseOption[]>(() => {
		if (!originShift) {
			return baseSwapNurseOptions;
		}
		return filterCompatibleSwapNurses(baseSwapNurseOptions, originShift);
	}, [baseSwapNurseOptions, originShift, filterCompatibleSwapNurses]);

	const originShiftOptions = useMemo(() => originShiftsForDate.filter((shift) => shift.nurseId === originNurse?.id), [originShiftsForDate, originNurse]);

	const swapShiftOptions = useMemo(
		() =>
			swapShiftsForDate.filter(
				(shift) => shift.nurseId === swapNurse?.id && (!jobManagementEnabled || !originShift?.jobId || shift.jobId === originShift.jobId),
			),
		[swapShiftsForDate, swapNurse, jobManagementEnabled, originShift?.jobId],
	);

	const [originWillCauseOvertime, setOriginWillCauseOvertime] = useState(DEFAULT_OT);
	const [swapWillCauseOvertime, setSwapWillCauseOvertime] = useState(DEFAULT_OT);

	useEffect(() => {
		const originNurseId = (lockedOriginNurse || originNurse)?.id;
		if (!originNurseId || !swapShift) {
			setOriginWillCauseOvertime(DEFAULT_OT);
			return;
		}
		let cancelled = false;
		backendService
			.predictCost({
				departmentId: chosenDepartment.department_id,
				nurseId: originNurseId,
				startTime: swapShift.startTime.toISO() ?? "",
				endTime: swapShift.endTime.toISO() ?? "",
				excludedShiftIds: originShift?.id ? [originShift.id] : [],
			})
			.then(({ data: prediction }) => {
				if (cancelled) return;
				const otDelta = prediction.overtimeHoursDelta;
				setOriginWillCauseOvertime({ originOT: toSwapOvertime(otDelta, prediction.overtimeType), swapOT: null });
			})
			.catch(() => {
				if (!cancelled) setOriginWillCauseOvertime(DEFAULT_OT);
			});
		return () => {
			cancelled = true;
		};
	}, [lockedOriginNurse, originNurse, swapShift, originShift?.id, chosenDepartment.department_id]);

	useEffect(() => {
		if (!swapNurse?.id || !originShift) {
			setSwapWillCauseOvertime(DEFAULT_OT);
			return;
		}
		let cancelled = false;
		backendService
			.predictCost({
				departmentId: chosenDepartment.department_id,
				nurseId: swapNurse.id,
				startTime: originShift.startTime.toISO() ?? "",
				endTime: originShift.endTime.toISO() ?? "",
				excludedShiftIds: swapShift?.id ? [swapShift.id] : [],
			})
			.then(({ data: prediction }) => {
				if (cancelled) return;
				const otDelta = prediction.overtimeHoursDelta;
				setSwapWillCauseOvertime({ originOT: null, swapOT: toSwapOvertime(otDelta, prediction.overtimeType) });
			})
			.catch(() => {
				if (!cancelled) setSwapWillCauseOvertime(DEFAULT_OT);
			});
		return () => {
			cancelled = true;
		};
	}, [swapNurse, originShift, swapShift?.id, chosenDepartment.department_id]);

	const checkNurseHasShiftOnDate = useCallback(
		(nurseId: number, date: DateTime): boolean => {
			const normalizedDate = date.startOf("day");
			return allAvailableShifts.some((shift) => {
				if (shift.nurseId !== nurseId) return false;
				const shiftDateInTz = shift.startTime.setZone(timezone);
				const shiftDate = getShiftDisplayDate(shiftDateInTz, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
				return shiftDate.toFormat("yyyy-MM-dd") === normalizedDate.toFormat("yyyy-MM-dd");
			});
		},
		[allAvailableShifts, timezone, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	useEffect(() => {
		if (initialShift) {
			if (initialOriginNurse) {
				setOriginNurse(initialOriginNurse);
			}
			setOriginShift(initialShift);
		}
	}, [initialShift, initialOriginNurse]);

	useEffect(() => {
		if (swapShiftOptions.length === 1 && !swapShift) {
			setSwapShift(swapShiftOptions[0]);
		}
		if (originShiftOptions.length === 1 && !originShift) {
			setOriginShift(originShiftOptions[0]);
		}
	}, [swapShiftOptions, swapShift, originShiftOptions, originShift]);

	useEffect(() => {
		if (originShift && !originShiftOptions.some((shift) => shift.id === originShift.id)) {
			setOriginShift(null);
		}
	}, [originShiftOptions, originShift]);

	useEffect(() => {
		if (swapShift && !swapShiftOptions.some((shift) => shift.id === swapShift.id)) {
			setSwapShift(null);
		}
	}, [swapShiftOptions, swapShift]);

	const handleOriginNurseChange = useCallback((nurse: NurseOption | null) => {
		setOriginNurse(nurse);
		setOriginShift(null);
	}, []);

	const handleSwapNurseChange = useCallback((nurse: NurseOption | null) => {
		setSwapNurse(nurse);
		setSwapShift(null);
	}, []);

	const handleOriginShiftChange = useCallback((shift: NurseInShift | null) => setOriginShift(shift), []);
	const handleSwapShiftChange = useCallback((shift: NurseInShift | null) => setSwapShift(shift), []);

	return {
		origin: {
			nurse: lockedOriginNurse || originNurse,
			shift: originShift,
			nurseOptions: originNurseOptions,
			shiftOptions: originShiftOptions,
			onNurseChange: handleOriginNurseChange,
			onShiftChange: handleOriginShiftChange,
			willCauseOvertime: originWillCauseOvertime,
		},
		swap: {
			nurse: swapNurse,
			shift: swapShift,
			nurseOptions: swapNurseOptions,
			shiftOptions: swapShiftOptions,
			onNurseChange: handleSwapNurseChange,
			onShiftChange: handleSwapShiftChange,
			willCauseOvertime: swapWillCauseOvertime,
		},
		hasShiftInDate: checkNurseHasShiftOnDate,
	};
};
